import React from "react";
import "./PriceCheckModal.css";
import { Vehicle } from "../../data/database";

interface PriceCheckModalProps {
    isOpen: boolean;
    onClose: () => void;
    vehicles: Vehicle[];
}

export default function PriceCheckModal({ isOpen, onClose, vehicles }: PriceCheckModalProps) {
    if (!isOpen) return null;

    // Simple mock price generator based on base model, year and mileage.
    const calculateEstimate = (v: Vehicle) => {
        // Determine base price based on model (very roughly 18-20m KRW for used electric trucks from 22-23)
        let basePrice = v.model === "현대 포터II 일렉트릭" ? 18000000 : 19000000;

        // Penalty for mileage (roughly -500 won per km)
        const mileagePenalty = v.mileage * 10;

        // Penalty for age (roughly -1m KRW per year older than 2024)
        const yearMatch = v.purchaseDate.match(/^(\d{4})/);
        const purchaseYear = yearMatch ? parseInt(yearMatch[1], 10) : 2022;
        const agePenalty = Math.max(0, (2025 - purchaseYear) * 1500000);

        let estimate = basePrice - mileagePenalty - agePenalty;
        // Floor it to realistic numbers
        if (estimate < 5000000) estimate = 5000000 + (Math.random() * 2000000);

        // Range padding
        const low = Math.floor((estimate * 0.95) / 10000) * 10000;
        const high = Math.floor((estimate * 1.05) / 10000) * 10000;

        return { low, high };
    };

    const formatKRW = (num: number) => {
        return new Intl.NumberFormat("ko-KR").format(num);
    };

    const handleConsultSubmit = () => {
        alert("매각 상담 신청이 완료되었습니다. 담당자가 곧 연락드리겠습니다.");
        onClose();
    };

    return (
        <div className="price-modal-backdrop">
            <div className="price-modal-container">
                {/* Header */}
                <div className="price-modal-header">
                    <div className="price-modal-title">
                        <h2>AI 중고차 시세 분석</h2>
                        <p>선택하신 {vehicles.length}대 차량의 현재 예상 매각가를 확인하세요.</p>
                    </div>
                    <button className="price-modal-close" onClick={onClose}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>

                {/* Content Body */}
                <div className="price-modal-body">
                    {vehicles.map((v) => {
                        const { low, high } = calculateEstimate(v);
                        const isGoodSoh = v.soh >= 90;

                        return (
                            <div key={v.vin} className="price-card">
                                {/* Vehicle Meta */}
                                <div className="price-card-meta">
                                    <div className="price-card-title-row">
                                        <h3>{v.vehicleNumber}</h3>
                                        <span className="price-card-model">{v.model}</span>
                                    </div>
                                    <div className="price-card-details">
                                        <span>{v.purchaseDate} 등록</span>
                                        <span className="dot-divider">•</span>
                                        <span>{formatKRW(v.mileage)} km</span>
                                        <span className="dot-divider">•</span>
                                        <span>{v.depot} 지점</span>
                                    </div>
                                </div>

                                {/* Badges */}
                                <div className="price-card-badges">
                                    <span className="badge badge-neutral">무사고 예측</span>
                                    {isGoodSoh && <span className="badge badge-success">배터리 우수 ({Math.round(v.soh)}%)</span>}
                                </div>

                                {/* Pricing Core */}
                                <div className="price-card-core">
                                    <div className="price-label">AI 예측 판매가 범위</div>
                                    <div className="price-value">
                                        <span className="price-accent">{formatKRW(low / 10000)}</span>만원 ~ <span className="price-accent">{formatKRW(high / 10000)}</span>만원
                                    </div>
                                </div>

                                {/* Graph/Bar Viz */}
                                <div className="price-card-viz">
                                    <div className="viz-label">
                                        <span>예상 감가율</span>
                                        <span>-{(100 - (high / 20000000) * 100).toFixed(1)}%</span>
                                    </div>
                                    <div className="viz-bar-bg">
                                        <div className="viz-bar-fill" style={{ width: `${(high / 25000000) * 100}%` }}></div>
                                    </div>
                                    <div className="viz-factors">
                                        <span className="factor down">주행거리 감가 발생</span>
                                        <span className="factor down">연식 감가 발생</span>
                                        {isGoodSoh && <span className="factor up">배터리 SOH 방어</span>}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Footer */}
                <div className="price-modal-footer">
                    <button className="pm-btn-secondary" onClick={onClose}>닫기</button>
                    <div className="pm-footer-actions">
                        <button className="pm-btn-outline" onClick={() => alert("상세 리포트 다운로드가 시작되었습니다.")}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                <polyline points="7 10 12 15 17 10"></polyline>
                                <line x1="12" y1="15" x2="12" y2="3"></line>
                            </svg>
                            상세 리포트 다운로드
                        </button>
                        <button className="pm-btn-primary" onClick={handleConsultSubmit}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                            </svg>
                            매각 상담 신청
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
